import { DOCUMENT } from '@angular/common';
import { DestroyRef, Inject, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  RedirectRequest
} from '@azure/msal-browser';
import { environment } from '@environment/environment';
import { HTTP_STATUS_CODE } from '@shared/constants/http-status-codes/http-status-codes.constant';
import { ROUTES } from '@shared/constants/routes/routes.constants';
import { CustomHttpError } from '@shared/interfaces/custom-http-error/custom-http-error.interface';
import { UserInfo } from '@shared/interfaces/user/user-info.interface';
import { UserService } from '@shared/service/user/user.service';
import { UserFacade } from '@store/user/user.facade';
import { catchError, concatMap, filter, map, Observable, of, shareReplay, take, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class OAuthService {
  private readonly destroyRef = inject(DestroyRef);

  private readonly userService = inject(UserService);
  private readonly userFacade = inject(UserFacade);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  /**
   * Memoised stream that completes as soon as MSAL has finished processing a
   * potential redirect response. Created lazily so that the (side-effecting)
   * `handleRedirectObservable()` call happens exactly once per page load, no
   * matter whether the router guard or `initializeAuth()` asks for it first.
   */
  private redirectHandled?: Observable<void>;

  private authInitialized = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG)
    private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {}

  public initializeAuth(): void {
    if (this.authInitialized) {
      return;
    }
    this.authInitialized = true;

    this.handelAccountAddedOrRemoved();

    this.redirectHandled$
      .pipe(
        map((): boolean => this.isLoggedIn()),
        tap((loggedIn: boolean) => {
          if (loggedIn) {
            this.checkAndSetActiveAccount();
          } else {
            this.login();
          }
        }),
        filter((loggedIn: boolean) => loggedIn),
        concatMap(() => this.userService.getUserInfo().pipe(take(1))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (user: UserInfo) => {
          this.userDataFetchedSuccessfully(user);
        },
        error: (err: CustomHttpError) => {
          this.useDataFetchedWithError(err);
        }
      });
  }

  /**
   * Resolves once MSAL has finished handling a redirect response and reports
   * whether an account is available. Guards must use this instead of the
   * synchronous `isLoggedIn()`, because during the very first navigation after
   * a login redirect the account cache is not populated yet: the authorization
   * code has not been exchanged for tokens at that point, so a synchronous
   * check inside a route guard would report "not logged in", cancel the
   * navigation and leave the user on a blank page.
   */
  public isAuthenticated$(): Observable<boolean> {
    return this.redirectHandled$.pipe(map((): boolean => this.isLoggedIn()));
  }

  private get redirectHandled$(): Observable<void> {
    this.redirectHandled ??= this.authService
      // `navigateToLoginRequestUrl` moved out of the global MSAL configuration
      // with @azure/msal-browser v5 and is now part of the redirect handling
      // options. Without it MSAL defaults to `true` and navigates back to the
      // page that started the login instead of staying on the configured
      // redirect URI, which is the behaviour the app relies on.
      .handleRedirectObservable({ navigateToLoginRequestUrl: false })
      .pipe(
        take(1),
        map((): void => undefined),
        // A failed redirect must not stall the application: fall through and let
        // the account check below decide what happens next.
        catchError((): Observable<void> => of(undefined)),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.redirectHandled;
  }

  public logout(): void {
    this.authService.logoutRedirect({
      idTokenHint: this.authService.instance.getActiveAccount()?.idToken
    });
  }

  public isLoggedIn(): boolean {
    return this.authService.instance.getAllAccounts().length > 0;
  }

  /**
   * Returns the account MSAL is currently working with. Used to pre-fill forms
   * (e.g. the user registration) with the data the user already provided during
   * the sign-up at the identity provider.
   */
  public getActiveAccount(): AccountInfo | null {
    return (
      this.authService.instance.getActiveAccount() ??
      this.authService.instance.getAllAccounts()[0] ??
      null
    );
  }

  /**
   * Resolves the claims of the current id token.
   *
   * `AccountInfo.idTokenClaims` is only populated when an id token for exactly
   * that account/tenant combination happens to be in the MSAL cache. Directly
   * after a redirect that is frequently not the case and the account only
   * carries its ids, with `name` and `username` being empty strings. Acquiring
   * a token silently (served from the cache, no network round trip when a valid
   * token is present) resolves the claims reliably.
   *
   * Never fails: if no claims can be resolved an empty object is emitted, so
   * callers can fall back to an empty result.
   */
  public getIdTokenClaims$(): Observable<Record<string, unknown>> {
    const account = this.getActiveAccount();

    if (!account) {
      return of({});
    }

    const cachedClaims = account.idTokenClaims as Record<string, unknown> | undefined;

    if (cachedClaims && Object.keys(cachedClaims).length > 0) {
      return of(cachedClaims);
    }

    return this.authService
      .acquireTokenSilent({
        account,
        scopes: environment.apiConfig.customBackend.scopes
      })
      .pipe(
        take(1),
        map(
          (result: AuthenticationResult) => (result.idTokenClaims ?? {}) as Record<string, unknown>
        ),
        catchError((): Observable<Record<string, unknown>> => of({}))
      );
  }

  private checkAndSetActiveAccount(): void {
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  private login(): void {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  private handelAccountAddedOrRemoved(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED ||
            msg.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          this.document.location.pathname = '/';
        }
      });
  }

  private userDataFetchedSuccessfully(user: UserInfo): void {
    if (user && (user?.admin || user.user)) {
      this.userFacade.userDataLoaded(user);
    } else {
      void this.router?.navigateByUrl('/' + ROUTES.USER_REGISTRATION);
    }
  }

  private useDataFetchedWithError(error: CustomHttpError): void {
    this.userFacade.userDataNotLoaded();
    if (error.status >= HTTP_STATUS_CODE.INTERNAL_SERVER_ERROR) {
      void this.router?.navigateByUrl('/' + ROUTES.SERVER_ERROR);
    }
  }
}
